import { inject, provide, ref } from 'vue'
import type { InjectionKey, Ref } from 'vue'
import * as mapsgl from '@xweather/mapsgl'
import type { MapboxMap } from '@/lib/map-x/webmap-viewer-types'

export interface XWeatherCredentials {
  clientId: string
  clientSecret: string
}

export interface XWeatherContext {
  controller: mapsgl.MapboxMapController | null
  isLoaded: Ref<boolean>
  initializeController: (map: MapboxMap) => void
}

const XWeatherKey: InjectionKey<XWeatherContext> = Symbol('xweather')

/**
 * Provides the XWeather controller context to the component tree.
 * Call this once at the app root (or another ancestor) before injecting it elsewhere.
 */
export function provideXWeather(credentials: XWeatherCredentials) {
  const account = new mapsgl.Account(
    credentials.clientId,
    credentials.clientSecret
  )

  let controller: mapsgl.MapboxMapController | null = null
  const isLoaded = ref(false)
  let initInProgress = false

  const initializeController = (map: MapboxMap) => {
    if (controller) {
      throw new Error('XWeather controller has already been initialized.')
    }

    if (initInProgress) {
      throw new Error(
        'XWeather controller initialization is already in progress.'
      )
    }

    initInProgress = true

    const newController = new mapsgl.MapboxMapController(map, { account })

    const onLoad = () => {
      controller = newController
      isLoaded.value = true
      initInProgress = false
      newController.off?.('load', onLoad)
    }

    const onError = () => {
      initInProgress = false
      isLoaded.value = false
      newController.off?.('error', onError)
    }

    newController.on('load', onLoad)
    newController.on?.('error', onError)
  }

  const context: XWeatherContext = {
    get controller() {
      return controller
    },
    isLoaded,
    initializeController,
  }

  provide(XWeatherKey, context)
  return context
}

/**
 * Injects the XWeather controller context.
 * Must be called within a component that is a descendant of a provider.
 */
export function injectXWeather(): XWeatherContext {
  const context = inject(XWeatherKey)
  if (!context) {
    throw new Error(
      'injectXWeather requires XWeather to be provided by an ancestor (typically the app root).'
    )
  }
  return context
}
